import { getStopDetails } from '../../Gantt/Popups/utils';
import reportConstants from '../../constants';
import { sortBy } from 'lodash';
import {
  getStopArrivalTime,
  getStopDepartureTime,
  getTimeStringFromDateTime,
  getTotalStopTime,
  getUniqueShipmentDeliveryCodes,
  isDelayStop,
  isStopCompleted,
  isTrailerDropHookStop
} from '../../util';
import { getStopStatusCodesWithDescriptions } from '../../stopStatusCodesUtils';
import { getStopServiceWindowByStopType } from "../../../../Dispatch/utils/utils";
import { v4 as uuidv4 } from 'uuid';
import StopActivityBadge from './StopActivityBadge';
import { RMAXLogger } from '../../../../../../services/LoggerService/RMAXLogger';

const getTotalStopsWithoutDelays = (stops) => {
  return stops.filter((stop) => !isDelayStop(stop)).length;
};

const getAdditionalStops = (stopName, time) => {
  return {
    activity: stopName,
    time: getTimeStringFromDateTime(time),
    id: uuidv4()
  };
};

const getActivityName = (stop) => {
  const { PRINT_ACTIVITY_TYPES } = reportConstants;
  const activityName = PRINT_ACTIVITY_TYPES[stop.stopType];
  if (isDelayStop(stop) || isTrailerDropHookStop(stop)) {
    return activityName;
  }
  return (
    <StopActivityBadge
      activityName={activityName}
      stopName={stop.name}
      stopSequence={stop.stopSequence}
      stopType={stop.stopType}
    />
  );
};

const getPrintReportStopStatus = (stopStatus, stopStatusCodes) => {
  const statusCodes = stopStatusCodes ? `(${stopStatusCodes})` : '';
  return `${stopStatus} ${statusCodes}`;
};

const getStopsData = (
  stops,
  routeData,
  clientCodeLists = {},
  enableReturnExceptionReasonLabels = false
) => {
  const { returnCodeList = [], exceptionList = [] } = clientCodeLists;
  const sortedStops = sortBy(stops, [
    (stop) => new Date(stop.actualArrivalTime ?? stop.plannedArrivalTime)
  ]);
  return sortedStops.map((stop) => {
    const isCompletedStop = isStopCompleted(stop);
    const isADelayStop = isDelayStop(stop);
    const { stopStatus } = isADelayStop ? { stopStatus: '' } : getStopDetails(stop);
    const codes = isADelayStop ? [] : getUniqueShipmentDeliveryCodes(stop);
    const stopStatusCodes = isADelayStop
      ? ''
      : getStopStatusCodesWithDescriptions(
          codes,
          returnCodeList,
          exceptionList,
          enableReturnExceptionReasonLabels
        );
    return {
      id: uuidv4(),
      time: '',
      activity: getActivityName(stop),
      arrivalTime: getTimeStringFromDateTime(getStopArrivalTime(stop, routeData)),
      departureTime: getTimeStringFromDateTime(getStopDepartureTime(stop, routeData)),
      plannedStopTime: isADelayStop
        ? stop.delayStopDuration
        : getTotalStopTime(stop.plannedArrivalTime, stop.plannedDepartureTime),
      actualStopTime: isCompletedStop
        ? getTotalStopTime(stop.actualArrivalTime, stop.actualDepartureTime)
        : '',
      status: isCompletedStop ? getPrintReportStopStatus(stopStatus, stopStatusCodes) : '',
      serviceWindow: getStopServiceWindowByStopType(stop),
      liftgate: stop.isLiftGateReqd ? 'LG' : ''
    };
  });
};

const filterStopsByStatus = (stops, status) =>
  stops.filter((stop) => stop.status?.includes(status)).length;

const getDataToPrint = (
  driverRouteData,
  driverData,
  clientCodeLists = {},
  enableReturnExceptionReasonLabels = false
) => {
  return driverRouteData.map((route) => {
    const routeStops = [];
    let routeData = {};
    driverData.clockInTime &&
      routeStops.push(getAdditionalStops('Clocked-in', driverData.clockInTime));
    route.driverDispatchTime &&
      routeStops.push(getAdditionalStops('Dispatched', route.driverDispatchTime));
    route.gateOutTime && routeStops.push(getAdditionalStops('Gate-out', route.gateOutTime));
    routeStops.push(
      ...getStopsData(
        route.stops,
        route,
        clientCodeLists,
        enableReturnExceptionReasonLabels
      )
    );
    route.gateInTime && routeStops.push(getAdditionalStops('Gate-in', route.gateInTime));
    routeData = {
      trailerNumber: route.trailerNumber,
      manifestNumber: route.manifestNumber,
      stops: [...routeStops],
      totalStops: getTotalStopsWithoutDelays(route.stops),
      totalStopsMadeStandard: filterStopsByStatus(routeStops, 'Made Standard'),
      totalStopsMissedStandard: filterStopsByStatus(routeStops, 'Missed Standard')
    };
    return routeData;
  });
};

const generateDPReportPrintLog = (driverNumber, routeIds) => {
  RMAXLogger.info(`Print Driver Performance Report`, {
    driverNumber,
    routeIds
  });
};

export { getDataToPrint, generateDPReportPrintLog };
